import { Box, Divider, Stack, Text } from 'braid-design-system';
import { useEffect, useState } from 'react';

import type { Partner } from 'src/types';

import { PartnerAccordion } from './PartnerAccordion';

interface Props {
  data: Record<Partner['type'], Partner[]>;
  searchFilter: string;
  solutionFilter: string[];
}

export const PartnerList = ({ data, searchFilter, solutionFilter }: Props) => {
  const [forceExpanded, setForceExpanded] = useState(false);
  const [filteredData, setFilteredData] = useState(data);
  const [count, setCount] = useState<number>(0);

  useEffect(() => {
    const filterData = (): typeof data => {
      if (searchFilter) {
        setForceExpanded(true);
        return Object.entries(data).reduce<typeof data>(
          (carry, [key, group]) => ({
            ...carry,
            [key]: group.filter((x) => x.name === searchFilter),
          }),
          {} as typeof data,
        );
      }
      setForceExpanded(false);

      if (solutionFilter) {
        return Object.entries(data).reduce<typeof data>(
          (carry, [key, group]) => {
            const newGroup = group.filter((x) => {
              const availableTypeCodes = x.configurableRelationships.map(
                ({ typeCode }) => typeCode,
              );
              const mappedValues = {
                applyWithSeek: availableTypeCodes.includes(
                  'APPLICATION_PREFILL',
                ),
                jobPosting: availableTypeCodes.includes('JOB_POSTING'),
                optimisedApply:
                  availableTypeCodes.includes('APPLICATION_EXPORT'),
              };

              return (
                Object.entries(mappedValues).filter(
                  ([solution, value]) =>
                    solutionFilter.includes(solution) && value,
                ).length === solutionFilter.length
              );
            });

            return { ...carry, [key]: newGroup };
          },
          {} as typeof data,
        );
      }

      return data;
    };

    const newData = filterData();
    setFilteredData(newData);
  }, [data, searchFilter, solutionFilter]);

  useEffect(() => {
    const sum = Object.values(filteredData)
      .map((item: any) => item.length)
      .reduce((a: number, b: number) => a + b);
    setCount(sum);
  }, [filteredData, searchFilter]);

  return (
    <>
      <Stack space="xlarge">
        <Box>
          <Stack space="large">
            <Text>
              <span>{count}</span>
              <span> Connected Partner</span>
              {count !== 1 && <span>s</span>}
              <span>:</span>
            </Text>
            <Divider />
          </Stack>
        </Box>

        <Stack space="gutter">
          <PartnerAccordion
            label="SEEK Premier Partners"
            partners={filteredData['SEEK Premier Partners']}
            summary="Premier Partners support direct access and the best experience with SEEK’s product solutions to help you get the most out of your recruitment software."
            forceExpanded={forceExpanded}
          />
          <PartnerAccordion
            label="SEEK Select Partners"
            partners={filteredData['SEEK Select Partners']}
            summary="Select Partners support access to SEEK’s product solutions to help you get the most out of your recruitment software."
            forceExpanded={forceExpanded}
          />
          <PartnerAccordion
            label="SEEK Access Partners"
            partners={filteredData['SEEK Access Partners']}
            summary="Access Partners include all other partners that support at least one of SEEK’s product solutions."
            defaultExpanded={false}
            forceExpanded={forceExpanded}
          />
        </Stack>
      </Stack>
    </>
  );
};
